import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogoMark } from "@/components/site/Logo";
import { EASE, lockScroll, scrollToTarget } from "@/lib/motion";

const LINKS = [
  { id: "work", label: "Work" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

const useSectionNav = (onDone) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (e, id) => {
    e.preventDefault();
    onDone?.();
    if (pathname === "/") {
      window.history.replaceState(null, "", `/#${id}`);
      scrollToTarget(`#${id}`);
    } else navigate(`/#${id}`);
  };
};

const MobileMenu = ({ open, onNav }) => (
  <AnimatePresence>
    {open && (
      <motion.nav
        key="menu"
        aria-label="Mobile"
        data-testid="mobile-menu"
        className="fixed inset-x-0 top-[57px] bottom-0 z-40 flex flex-col justify-between bg-paper px-6 pb-10 pt-8 md:hidden"
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <ul className="border-t border-ink/15">
          {LINKS.map((l, i) => (
            <li key={l.id} className="border-b border-ink/15">
              <a
                href={`/#${l.id}`}
                onClick={(e) => onNav(e, l.id)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="flex items-baseline justify-between py-5 font-display text-5xl font-medium tracking-[-0.04em]"
              >
                {l.label}
                <span className="label text-mute">0{i + 1}</span>
              </a>
            </li>
          ))}
        </ul>
        <p className="label text-mute">Hertfordshire, UK</p>
      </motion.nav>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    setOpen(v);
    lockScroll(v);
  };
  const onNav = useSectionNav(() => toggle(false));
  return (
    <header data-testid="navigation-header" className="sticky top-0 z-50 border-b border-ink/15 bg-paper">
      <div className="flex h-14 items-center justify-between px-6 lg:px-12">
        <Link to="/" onClick={() => toggle(false)} data-testid="header-home-link" className="group flex items-center gap-3" aria-label="Eldiiar Dzhumagaziev — home">
          <LogoMark className="h-6 w-6 transition-transform duration-700 group-hover:rotate-90" />
          <span className="text-[13px] font-medium tracking-[0.04em]">ELDIIAR DZHUMAGAZIEV</span>
        </Link>
        <nav aria-label="Primary" className="hidden md:block">
          <ul className="flex gap-10 text-[13px] font-medium">
            {LINKS.map((l) => (
              <li key={l.id}>
                <a href={`/#${l.id}`} onClick={(e) => onNav(e, l.id)} data-testid={`nav-link-${l.id}`} className="u-link pb-0.5">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <button
          type="button"
          className="label md:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => toggle(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>
      <MobileMenu open={open} onNav={onNav} />
    </header>
  );
};
